import CHAT_TEMPLATES, { getContextSuggestions } from '../config/chatTemplates';
import ROLE_SUGGESTIONS from '../config/roleSuggestions';

const CATEGORY_LABELS = {
  hr: 'Nhân sự',
  vehicle: 'Quản lý xe',
  work: 'Công việc',
  personal: 'Cá nhân'
};

const ROLE_CATEGORY_MAP = {
  SUPER_ADMIN: ['hr', 'vehicle', 'work'],
  ADMIN: ['hr', 'vehicle', 'work'],
  LEVEL_II_MANAGER: ['hr', 'vehicle', 'work'],
  LEVEL_I_MANAGER: ['hr', 'work'],
  REPORT_MANAGER: ['hr', 'work'],
  ACCOUNTANT: ['hr', 'work'],
  EMPLOYEE: ['personal', 'work']
};

const SUPER_ADMIN_RAW_MODE_SUGGESTION = {
  q: 'RAW mode realtime: phân tích toàn bộ dữ liệu hệ thống và trả lời theo dữ liệu mới nhất.',
  label: 'RAW realtime + AI',
  icon: 'fa-database',
  source: 'super-admin-raw-mode'
};

const FOLLOW_UP_PATTERNS = [
  {
    keyword: /hợp đồng|hop dong/i,
    suggestion: { q: 'Ưu tiên xử lý hợp đồng nào trước trong tuần này?', label: 'Ưu tiên hợp đồng', icon: 'fa-file-contract' }
  },
  {
    keyword: /đi muộn|di muon|chấm công|cham cong/i,
    suggestion: { q: 'Đề xuất 3 hành động để giảm tình trạng đi muộn?', label: 'Giảm đi muộn', icon: 'fa-user-clock' }
  },
  {
    keyword: /nhiên liệu|nhien lieu|xăng|fuel/i,
    suggestion: { q: 'Xe nào cần kiểm tra kỹ vì tiêu hao nhiên liệu bất thường?', label: 'Xe tiêu hao cao', icon: 'fa-gas-pump' }
  },
  {
    keyword: /bảo dưỡng|bao duong|maintenance/i,
    suggestion: { q: 'Lên lịch bảo dưỡng ưu tiên cho 7 ngày tới giúp tôi.', label: 'Lịch bảo dưỡng 7 ngày', icon: 'fa-tools' }
  },
  {
    keyword: /tuyển dụng|tuyen dung|ứng viên|ung vien/i,
    suggestion: { q: 'Tỉ lệ chuyển đổi tuyển dụng theo từng vị trí là bao nhiêu?', label: 'Tỉ lệ chuyển đổi', icon: 'fa-user-plus' }
  }
];

const dedupeByQuestion = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    if (!item?.q) return;
    const key = item.q.trim().toLowerCase();
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

export const getTemplatesForRole = (role = 'EMPLOYEE') => {
  const categories = ROLE_CATEGORY_MAP[role] || ROLE_CATEGORY_MAP.EMPLOYEE;

  return Object.values(CHAT_TEMPLATES)
    .filter((template) => categories.includes(template.category))
    .sort((left, right) => left.title.localeCompare(right.title, 'vi'));
};

export const getTemplateSectionsForRole = (role = 'EMPLOYEE') => {
  const templates = getTemplatesForRole(role);
  const grouped = templates.reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    acc[template.category].push(template);
    return acc;
  }, {});

  return Object.entries(grouped).map(([category, items]) => ({
    category,
    label: CATEGORY_LABELS[category] || category,
    items
  }));
};

export const getSmartSuggestions = ({ role = 'EMPLOYEE', dataSnapshot = {}, messages = [] }) => {
  const now = new Date();
  const contextual = getContextSuggestions(
    role,
    now.getHours(),
    now.getDay(),
    now.getDate(),
    {
      expiringContracts: dataSnapshot?.expiringContracts || 0,
      overdueTasks: dataSnapshot?.overdueTasks ?? dataSnapshot?.overdueTask ?? 0,
      vehicleExpiring: dataSnapshot?.vehicleExpiring || 0,
      fuelCostHigh: Boolean(dataSnapshot?.fuelCostHigh)
    }
  );

  const roleDefaults = (ROLE_SUGGESTIONS[role] || ROLE_SUGGESTIONS.EMPLOYEE || { questions: [] }).questions
    .slice(0, 6)
    .map((item) => ({
      q: item.q,
      label: item.label,
      icon: item.icon,
      source: 'role-default'
    }));

  const lastAssistantMessage = [...messages].reverse().find((item) => item?.role === 'assistant' && item?.content);
  const followUps = FOLLOW_UP_PATTERNS
    .filter((pattern) => pattern.keyword.test(String(lastAssistantMessage?.content || '')))
    .map((pattern) => ({
      ...pattern.suggestion,
      source: 'follow-up'
    }));

  const combined = dedupeByQuestion([
    ...followUps,
    ...contextual.map((item) => ({ ...item, source: 'context' })),
    ...roleDefaults
  ]);

  if (role === 'SUPER_ADMIN') {
    return dedupeByQuestion([
      SUPER_ADMIN_RAW_MODE_SUGGESTION,
      ...combined
    ]).slice(0, 8);
  }

  return combined.slice(0, 8);
};

export const findTemplateById = (templateId) => CHAT_TEMPLATES[templateId] || null;
